import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { unparse } from "papaparse";
import FeatherIcon from "feather-icons-react";
import { Button } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import {
  DoubleMaterialityESRSAreaEnum,
  DoubleMaterialityIroTypeEnum,
  DoubleMaterialityQuestionTimeHorizon,
} from "@green0meter/api-client";

import { getQuestionIro } from "./getQuestionIro";

import { getYearFromSearchParams } from "@helpers";
import {
  DoubleMaterialityResultRow,
  DoubleMaterialityConsolidatedResultRow,
} from "@api/hooks/doubleMateriality";
import { QuestionAnswerRow } from "@api/hooks/doubleMateriality/GetQuestionAnswers/useGetQuestionAnswers";
import { useGetAssessments } from "@api";
import { useUserStore } from "@store/user";

interface DMAggregatedResultsExportButtonProps {
  tableRows: (DoubleMaterialityResultRow | DoubleMaterialityConsolidatedResultRow)[];
  answers: QuestionAnswerRow[];
  isTableLoading: boolean;
  isConsolidatedView: boolean;
  thresholds: {
    externalImpact: number;
    internalFinancial: number;
  };
}

/**
 * exports aggregated results to a CSV file. This can be either consolidated or just for your current company
 */
export const DMAggregatedResultsExportButton: FC<
  DMAggregatedResultsExportButtonProps
> = ({ tableRows, answers, isTableLoading, isConsolidatedView, thresholds }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUserStore();

  const [searchParams] = useSearchParams();
  const year = getYearFromSearchParams(searchParams);

  const { assessments, isPendingGetAssessments } = useGetAssessments(user, {
    year,
    pageNumber: 1,
    pageSize: 1000,
  });

  const handleResultsCsvExport = (): void => {
    if (!tableRows.length) {
      enqueueSnackbar(t("doubleMateriality.export.noData", "No data to export"), {
        variant: "warning",
      });

      return;
    }

    if (isPendingGetAssessments) {
      enqueueSnackbar(t("doubleMateriality.export.loadingData", "Loading data..."), {
        variant: "info",
      });

      return;
    }

    const yesLabel = t("common.yes", "Yes");
    const noLabel = t("common.no", "No");

    const csvRows = tableRows.map((row) => {
      const assessment = assessments?.data?.find(
        (a) => a.assessmentId === row.assessmentFriendlyId,
      );

      const esrsCode = DoubleMaterialityESRSAreaEnum[row.topic] ?? row.topic;
      const esrsLabel = String(esrsCode);
      const impactValue = row.impactLevel?.override ?? row.impactLevel?.value ?? 0;
      const riskValue = row.riskLevel?.override ?? row.riskLevel?.value ?? null;
      const opportunityValue =
        row.opportunityLevel?.override ?? row.opportunityLevel?.value ?? null;
      const isRiskMaterial =
        riskValue != null && Math.abs(riskValue) >= thresholds.internalFinancial;
      const isOpportunityMaterial =
        opportunityValue != null &&
        Math.abs(opportunityValue) >= thresholds.internalFinancial;
      const isBelowExternal = impactValue < thresholds.externalImpact;
      const isMaterial = isRiskMaterial || isOpportunityMaterial || !isBelowExternal;

      // Purple fields logic
      const rowAnswers = answers.filter(
        (a) =>
          assessment &&
          a.question?.assessment?.sustainabilityMatter ===
            assessment.sustainabilityMatter,
      );

      const impactAnswers = rowAnswers.filter(
        (a) => getQuestionIro(a.question) === DoubleMaterialityIroTypeEnum.Impact,
      );
      const riskAnswers = rowAnswers.filter(
        (a) => getQuestionIro(a.question) === DoubleMaterialityIroTypeEnum.Risk,
      );
      const oppAnswers = rowAnswers.filter(
        (a) =>
          getQuestionIro(a.question) === DoubleMaterialityIroTypeEnum.Opportunity,
      );

      // Real/Potential
      const realCount = impactAnswers.filter((a) => a.isReal === true).length;
      const potentialCount = impactAnswers.filter((a) => a.isReal === false).length;
      const realPotential =
        realCount > potentialCount
          ? "Real"
          : potentialCount > realCount
            ? "Potential"
            : "-";

      // Negative/Positive
      const posCount = impactAnswers.filter(
        (a) => a.isPositiveImpact === true,
      ).length;
      const negCount = impactAnswers.filter(
        (a) => a.isPositiveImpact === false,
      ).length;
      const negPos =
        posCount > negCount ? "Positive" : negCount > posCount ? "Negative" : "-";

      // Time Horizon
      const getMostCommonTimeHorizon = (ans: QuestionAnswerRow[]): string => {
        const counts: Record<number, number> = {
          [DoubleMaterialityQuestionTimeHorizon.Short]: 0,
          [DoubleMaterialityQuestionTimeHorizon.Medium]: 0,
          [DoubleMaterialityQuestionTimeHorizon.Long]: 0,
        };
        let hasData = false;

        ans.forEach((a) => {
          a.timeHorizon?.forEach((th: DoubleMaterialityQuestionTimeHorizon) => {
            if (
              th >= DoubleMaterialityQuestionTimeHorizon.Short &&
              th <= DoubleMaterialityQuestionTimeHorizon.Long
            ) {
              counts[th] = (counts[th] || 0) + 1;
              hasData = true;
            }
          });
        });

        if (!hasData) {
          return "-";
        }

        let maxCount = 0;

        Object.values(counts).forEach((c) => {
          if (c > maxCount) maxCount = c;
        });

        if (maxCount === 0) return "-";

        const modes: number[] = [];

        (
          [
            DoubleMaterialityQuestionTimeHorizon.Short,
            DoubleMaterialityQuestionTimeHorizon.Medium,
            DoubleMaterialityQuestionTimeHorizon.Long,
          ] as const
        ).forEach((key) => {
          if (counts[key] === maxCount) {
            modes.push(key);
          }
        });

        const mapping: Record<number, string> = {
          [DoubleMaterialityQuestionTimeHorizon.Short]: "Short",
          [DoubleMaterialityQuestionTimeHorizon.Medium]: "Medium",
          [DoubleMaterialityQuestionTimeHorizon.Long]: "Long",
        };

        return modes.map((m) => mapping[m]).join("; ");
      };

      const impactTime = getMostCommonTimeHorizon(impactAnswers);
      const riskTime = getMostCommonTimeHorizon(riskAnswers);
      const oppTime = getMostCommonTimeHorizon(oppAnswers);

      // Add metrics
      const formatValue = (val: number | null | undefined): string =>
        val != null && !Number.isNaN(val) ? val.toFixed(2) : "";

      interface MetricData {
        value?: number | null;
        override?: number | null;
        average?: number | null;
      }

      type MetricKey =
        | "impactLevel"
        | "riskLevel"
        | "opportunityLevel"
        | "externalLevel";

      const getMetricValues = (
        key: MetricKey,
      ): { value: string; override: string } => {
        const m = (row as unknown as Record<MetricKey, MetricData>)[key];

        return {
          value: formatValue(m?.average ?? m?.value),
          override: formatValue(m?.override),
        };
      };

      const impactM = getMetricValues("impactLevel");
      const riskM = getMetricValues("riskLevel");
      const oppM = getMetricValues("opportunityLevel");
      const extM = getMetricValues("externalLevel");

      const rowData: Record<string, string | number> = {
        [t("doubleMateriality.results.esrs", "ESRS")]: esrsLabel,
        [t("doubleMateriality.results.assessmentId", "Assessment ID")]:
          row.assessmentFriendlyId,
        [t(
          "doubleMateriality.results.sustainabilityMatter",
          "Sustainability matter",
        )]: row.sustainabilityMatter,
        [t("doubleMateriality.results.subTopic", "Sub-topic")]: row.subTopic ?? "",
        [t("doubleMateriality.results.subSubTopic", "Sub-sub-topic")]:
          row.subSubTopic ?? "",
        "Real/Potential": realPotential,
        "Negative/Positive": negPos,
        "Impact Time Horizon": impactTime,
        [t("doubleMateriality.results.impactLevel", "Impact level")]: impactM.value,
        [`${t("doubleMateriality.results.impactLevel", "Impact level")} (${t(
          "common.override",
          "Override",
        )})`]: impactM.override,
        "Risk Time Horizon": riskTime,
        [t("doubleMateriality.results.riskLevel", "Risk level")]: riskM.value,
        [`${t("doubleMateriality.results.riskLevel", "Risk level")} (${t(
          "common.override",
          "Override",
        )})`]: riskM.override,
        "Opportunity Time Horizon": oppTime,
        [t("doubleMateriality.results.opportunityLevel", "Opportunity level")]:
          oppM.value,
        [`${t(
          "doubleMateriality.results.opportunityLevel",
          "Opportunity level",
        )} (${t("common.override", "Override")})`]: oppM.override,
        [t("doubleMateriality.results.externalLevel", "External level")]: extM.value,
        [`${t("doubleMateriality.results.externalLevel", "External level")} (${t(
          "common.override",
          "Override",
        )})`]: extM.override,
      };

      rowData[t("doubleMateriality.results.isMaterial", "Is material")] = isMaterial
        ? yesLabel
        : noLabel;

      if (isConsolidatedView && "combinedEntityNames" in row) {
        rowData[t("doubleMateriality.results.entities", "Entities")] =
          (row as DoubleMaterialityConsolidatedResultRow).combinedEntityNames ?? "";
      }

      // Add fields from DoubleMaterialityAssessment entity
      if (assessment) {
        const stripHtml = (html: string | undefined): string => {
          if (!html) return "";
          const tmp = document.createElement("DIV");

          tmp.innerHTML = html;

          return tmp.textContent || tmp.innerText || "";
        };

        rowData[
          t("doubleMateriality.results.relevantSubjects", "Relevant subjects")
        ] = assessment.relevantSubjects?.map((s) => s.name).join(", ") ?? "";
        rowData["Part of Value Chain"] =
          assessment.partOfValueChain?.join(", ") ?? "";
        rowData["Impacts"] = stripHtml(assessment.impacts);
        rowData["Risks"] = stripHtml(assessment.risks);
        rowData["Opportunities"] = stripHtml(assessment.opportunities);
        rowData["Sources"] = stripHtml(assessment.sources);
        rowData["Attachments"] = assessment.attachments?.join(", ") ?? "";
      }

      return rowData;
    });

    const csv = unparse(csvRows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");

    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);

      link.href = url;
      link.download = `double-materiality-aggregated-results-${year}-${
        new Date().toISOString().split("T")[0]
      }.csv`;
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      enqueueSnackbar(t("doubleMateriality.export.success", "Export successful"), {
        variant: "success",
      });
    }
  };

  return (
    <Button
      variant="text"
      color="primary"
      size="small"
      startIcon={<FeatherIcon icon="download" size={14} />}
      onClick={handleResultsCsvExport}
      disabled={isTableLoading || isPendingGetAssessments}
      sx={{
        textTransform: "none",
        whiteSpace: "nowrap",
        fontWeight: 600,
        minHeight: 32,
      }}
    >
      {t("doubleMateriality.exportCSV", "Export")}
    </Button>
  );
};
